<script setup>
import { onMounted, onUnmounted } from 'vue'
import { Background } from '@vue-flow/background'
import { MiniMap } from '@vue-flow/minimap'
import { VueFlow, useVueFlow, ConnectionMode, Position } from '@vue-flow/core'
import { usePipelineStore } from '@/stores/pipeline'

import InteractionControls from '@/components/InteractionControls.vue'
import DefaultNode from '@/components/DefaultNode.vue'
import CustomEdge from '@/components/CustomEdge.vue'

const {
  addEdges,
  onConnect,
  onEdgesChange,
  onNodeClick,
  onNodesChange,
  onPaneReady,
  toObject,
  removeNodes,
  removeEdges
} = useVueFlow()

const pipelineStore = usePipelineStore()

let flowInstance = undefined

const handleLoadPipelineEvent = async ({ detail }) => {
  console.log(`Loading pipeline ${detail.id}`)
  await pipelineStore.fetchPipeline(detail.id)
}

const props = defineProps({
  pipelineId: String,
  parentController: Object
})

const handleStateChange = () => {
  pipelineStore.putPipeline(props.pipelineId, toObject())
  pipelineStore.fetchPipeline(props.pipelineId)
}

const handleNodeEdited = (id) => {
  // Send message to Stimulus controller
  props.parentController.dispatch("nodeDoubleClicked", { detail: { id } })
}

const handleNodeRemoved = (id) => {
  removeNodes([id])
  handleStateChange()
}

const addNode = (event) => {
  props.parentController.dispatch("addNewClicked", { detail: {} })
}

const handleEdgeRemove = (edgeId) => {
  removeEdges([edgeId])
  handleStateChange()
}

onMounted(async () => {
  document.addEventListener("loadPipeline", handleLoadPipelineEvent)
  await pipelineStore.fetchPipeline(props.pipelineId)
})

onNodeClick(({ event, node }) => {
  props.parentController.dispatch("nodeDoubleClicked", { detail: { id: node.id } })
});

onUnmounted(() => {
  document.removeEventListener("loadPipeline", handleLoadPipelineEvent)
})

onPaneReady((instance) => {
  flowInstance = instance
})

onEdgesChange((edges) => {
  const removedEdges = edges.filter(edge => edge.type === 'remove')
  if (removedEdges.length) { handleStateChange() }
})

onConnect((params) => {
  addEdges([{...params, ...{animated: true}}])

  handleStateChange()
})

onNodesChange((changes) => {
  let stateChanged = false
  changes.forEach((change) => {
    if (change.type == "remove") {
      stateChanged = true
    }
  })

  if (stateChanged) handleStateChange()
})

</script>

<template>
  <div style="height: calc(100vh - 180px);">
    <VueFlow
      :nodes="pipelineStore.nodes"
      :edges="pipelineStore.edges"
      :connection-mode="ConnectionMode.Strict"
      :delete-key-code="false"
      fit-view-on-init
      :disableKeyboardA11y="true"
    >
      <div class="absolute z-10 w-[200px] right-2 top-2 flex flex-col items-end">
        <button class="btn" @click="addNode">Add New</button>
      </div>
      <InteractionControls
        @node-double-clicked="handleNodeEdited"
        @node-edited="handleNodeEdited"
        @node-removed="handleNodeRemoved"
      />
      <MiniMap />
      <Background />

      <template #node-default="nodeProps">
        <DefaultNode
          v-bind="nodeProps"
          @edit-node="handleNodeEdited"
          @remove-node="handleNodeRemoved"
        />
      </template>

      <template #node-input="nodeProps">
        <DefaultNode
          v-bind="nodeProps"
          @edit-node="handleNodeEdited"
          @remove-node="handleNodeRemoved"
        />
      </template>

      <template #node-parallel="nodeProps">
        <DefaultNode
          v-bind="nodeProps"
          @edit-node="handleNodeEdited"
          @remove-node="handleNodeRemoved"
        />
      </template>

      <template #edge-default="edgeProps">
        <CustomEdge v-bind="edgeProps" @remove="handleEdgeRemove" />
      </template>
    </VueFlow>
  </div>
</template>

<style>
/* import the necessary styles for Vue Flow to work */
@import '@vue-flow/core/dist/style.css';

/* import the default theme, this is optional but generally recommended */
@import '@vue-flow/core/dist/theme-default.css';

.vue-flow__node-default, .vue-flow__node-input, .vue-flow__node-output, .vue-flow__node-parallel {
  padding: 10px;
  width: 300px;
  height: auto;
  background-color: var(--color-text-primary) !important;
  color: var(--color-bg-primary) !important;
  border-color: var(--color-border-primary) !important;
}

.vue-flow__node-default.selected,
.vue-flow__node-input.selected,
.vue-flow__node-output.selected,
.vue-flow__node-parallel.selected {
  background-color: var(--color-text-primary) !important;
  opacity: 0.9;
  box-shadow: 0 0 0 2px var(--color-border-primary) !important;
}

.vue-flow__minimap {
  z-index: 1000;
  background-color: var(--color-bg-primary-base);
}

.vue-flow__edge-interaction-circle {
  pointer-events: all;
}
</style>
